import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';

import jwt from 'jsonwebtoken';
import { v4 as uuidv4 } from 'uuid';
import { loadStripe, PaymentIntentResult, StripeCardNumberElementOptions } from '@stripe/stripe-js';
import {
	CardNumberElement,
	CardCvcElement,
	CardExpiryElement,
	Elements,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js';

import classNames from 'classnames';
import { completeRegistrationProfile, register, setRegistrationStep, createStripeIntent, finishedStripePayment, reRegister } from '../../../store/actions/event/event-actions';
import { EventsState } from '../../../store/types';
import { TemplateClassNames, TemplateNames } from '../../../types/template-layouts';
import {
	BlProfile,
	BrandliveEvent,
	EPreviewTypes,
	LanguagesAbbr,
	QuestionType,
	RegistrationQuestion,
	RegistrationQuestionType,
	RegistrationStep,
	RegistrationStepType,
	RegistrationTypes,
	TicketingSet,
	TranslateString,
} from '../../../types/working-model';
import { getStartTime } from '../../../utils/get-event-start-time';
import { EIcon } from '../../general-ui/icon/icon';
import SelectNative, { SelectOption } from '../../general-ui/select/select-native';
import { TypographyItem } from '../../general-ui/typography-item/typography-item';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import { eventPath } from '../utils';
import {
	RegistrationFooter,
	RegistrationFooterProps,
} from './registration-footer';
import { RegistrationLayout } from './registration-layout/registration-layout';
import './registration.scss';
import { GetUploadEndpoint, MakeItemPublic, UploadToS3 } from '../../../connection/uploads';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import CustomCheckbox from '../../general-ui/checkbox/custom-checkbox';
import { isValidEmail } from '../../../utils/utils';
import '../../general-ui/text-input/input.scss';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { Field, FieldLabel } from '../../general-ui/field/field';
import { showAlert } from '../../general-ui/alert/alert-service';
import { useTranslation } from 'react-i18next';
import useTranslationModules from '../../../i18n/useTranslationModules';
import { ParamsProps } from '../live-event';

import Textarea from '../../general-ui/textarea/textarea';
import { ICONS } from '../../general-ui/icon';

import { getStorageItem, getStorageObject, removeStorageItem, setStorageObject } from '../../../utils/local-storage';
import { Button } from '../../general-ui/button/button';
import { parseColor } from '../../admin/document-head';

const getOptions = (options: string[]): SelectOption[] => {
	return options.map((o) => ({
		label: o,
		value: o,
	}));
};

enum BlProfileValues {
	firstName = "1",
	lastName = "2",
	email = "3"
}

// type GlobalRegField = "registration.registration_fields.1" | "registration.registration_fields.2" | "registration.registration_fields.3" | "registration.registration_fields.4" | "registration.registration_fields.5" | "registration.registration_fields.6" | "registration.registration_fields.7" | "registration.registration_fields.8" | "registration.registration_fields.9" | "registration.registration_fields.10" | "registration.registration_fields.11" | "registration.registration_fields.18" | "registration.registration_fields.21" | "registration.registration_fields.38" | "registration.registration_fields.47";
interface IRegistrationFormField {
	type: string;
	field_id: number;
	label: string;
	placeholder: string;
	options?: SelectOption[];
	required: boolean;
	persistent: boolean;
	global: boolean;
	translation?: TranslateString;
}

const parseFields = ({
	questions,
	requiredFields,
	language
}: {
	questions: RegistrationQuestion[];
	requiredFields: number[];
	language: string;
}): IRegistrationFormField[] => {
	const data =
		questions?.map(({ name, registration_question, type, options, persistent, global, translation }) => {
			const result: IRegistrationFormField = {
				type,
				field_id: registration_question,
				label: (translation?.[language] as string) ?? name,
				placeholder: (translation?.[language] as string) ?? name,
				options: options ? getOptions(options) : undefined,
				required: requiredFields?.includes(registration_question),
				persistent,
				global,
				translation
			};
			return result;
		}) || [];
	return [
		...data,
	];
};

interface RegistrationPageProps {
	previewLanguage?: LanguagesAbbr
}

const RegistrationPage: React.FC<RegistrationPageProps> = ({ previewLanguage })
	: JSX.Element => {
	const {
		LiveEventReducer: {
			eventBundle,
			registrationStep,
			finishedRegistering,
			blProfileUserToken,
			registering,
			completingProfile,
			registrationError,
			blProfileUser,
			stripeIntent,
			stripeIntentError,
			registrationId,
			mustVerify,
			shouldRedirectToRegistration,

		},
		CreateEventReducer: {
			workingEvent,
			previewMode,
			registrationStep: previewRegistrationStep
		}
	} = useSelector((event: EventsState) => event);
	const { uuid, eventName, language }: ParamsProps = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const stripe = useStripe();
	const stripeElements = useElements();
	const location: any = useLocation();

	const [selectedTicket, setSelectedTicket] = useState({});
	const [signIn, setSignIn] = useState(false);


	//check to make sure language is in translated list, if not, push them to the default language
	//in this case english is currently locked as the homepage and registration default
	const notValidTranslation = !eventBundle?.homepage?.languages.includes(language);
	const languageToUse = previewLanguage //if in preview module/portal use the passed language beacuse params is not avalable
		? previewLanguage
		: notValidTranslation ? "en" : language;

	useTranslationModules({ language: languageToUse });

	const isPreview = previewMode === 'registration';

	const [/* registrationData */, setRegistrationData] = useState<TRegistrationData>(
		{} as TRegistrationData
	);
	const registration = isPreview ? workingEvent?.registration_settings : eventBundle?.registration_settings;
	const registrationSteps = isPreview ? workingEvent?.registration_steps : eventBundle?.registration_steps;
	const singleSignOns = isPreview ? workingEvent?.registration_settings?.singleSignOns : eventBundle?.registration_settings?.singleSignOns;
	const requiredFields = isPreview ? workingEvent?.required_registration_questions : eventBundle?.required_registration_questions;
	const template = (isPreview ? workingEvent?.template.name : eventBundle?.template.name) || TemplateNames.Classic;
	const logo = isPreview ? workingEvent?.registration_settings?.logo : eventBundle?.registration_settings?.logo;
	const stripeIntentChecked = useRef(false);

	const startDateTicks = isPreview ? (workingEvent ? getStartTime(workingEvent, false) : undefined) : (eventBundle ? getStartTime(eventBundle, false) : undefined);
	const startDate = startDateTicks ? new Date(startDateTicks) : undefined;
	const activeSteps = useMemo(
		() => registrationSteps?.filter((step) => step.isOn) || [],
		[registrationSteps]
	);

	// const ticketingStep = activeSteps.findIndex(step => step.ticketing);
	const isFinalStep = registrationStep >= activeSteps.length - 1;
	const isFirstStep = registrationStep === 0;
	const activeStep = activeSteps?.[registrationStep];

	// const localStorageRegStepKey = `registration-step-${eventBundle?.uuid}`;

	useEffect(() => {
		//check to make sure language is in translated list, if not, push them to the default language
		//in this case english is currently locked as the homepage and registration default
		if (notValidTranslation && !previewMode) {
			history.replace(`/${eventPath(eventName, uuid)}/en/registration`);
		}
	}, [history, eventName, uuid, notValidTranslation, previewMode]);

	useEffect(() => {
		// on first render of preview, set active step to match current reg step in editor
		if (previewMode === EPreviewTypes.Registration && activeSteps.length) {
			const startingStep = activeSteps.findIndex(step => step.type === previewRegistrationStep);
			dispatch(setRegistrationStep(startingStep));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		//if user has refreshed the page after registering BUT has been redirected to the 
		//payment form, we need to know what the last ticket they selected was
		if (eventBundle) {
			const storedTicket = getStorageObject(`selected-ticket.${eventBundle!.uuid}`);

			if (storedTicket) {
				setSelectedTicket(storedTicket);
			}
		}
	}, [eventBundle]);

	const handlePrevStep = useCallback((e: any) => {
		e.preventDefault();
		if (isFirstStep) return;
		dispatch(setRegistrationStep(registrationStep - 1));
	}, [isFirstStep, dispatch, registrationStep]);

	const handleSaveData = useCallback((data: SubmissionValues) => {
		// if in preview mode don't submit but progress through registration
		if (previewMode === EPreviewTypes.Registration) {
			if (isFinalStep) return;
			return dispatch(setRegistrationStep(registrationStep + 1));
		}
		setRegistrationData((prev) => ({ ...prev, ...data }));
		//drop "/registration" from the current path to redirect the user back to the page.
		const path = window.location.href.split("/");
		path.pop();
		const source = path.join("/");

		switch (activeStep.type) {
			case RegistrationStepType.general: {
				const userCode = getStorageItem('user-code');

				//only put something in storage if there's an actual key here
				if (data.ticket && Object.keys(data.ticket).length > 0) {
					setSelectedTicket(data.ticket);

					//set the ticket they selected in local storage in case they refresh
					setStorageObject(`selected-ticket.${eventBundle!.uuid}`, data.ticket);

					delete data.ticket;
				}

				return dispatch(register({
					fields: data,
					lang: language,
					source: source,
					userCode: userCode,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					isFinalStep: isFinalStep
				}, eventBundle!.uuid, blProfileUserToken));
			}
			case RegistrationStepType.profile: {
				//if this is the final step, we must send the email
				const options = {
					fields: data,
					lang: language,
					avatar: null,
					channel: eventBundle!.channel,
					isFinalStep: isFinalStep,
					source: source,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					eventUuid: eventBundle!.uuid,
					registrationId: registrationId as string
				};
				return dispatch(completeRegistrationProfile(options, blProfileUserToken));
			}
			case RegistrationStepType.avatar: {
				//if this is the final step, we must send the email
				const options = {
					fields: data,
					lang: language,
					avatar: null,
					channel: eventBundle!.channel,
					isFinalStep: isFinalStep,
					source: source,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					eventUuid: eventBundle!.uuid,
					registrationId: registrationId as string
				};
				return dispatch(completeRegistrationProfile(options, blProfileUserToken));
			}
			case RegistrationStepType.ticketing: {
				const options = {
					channel_id: eventBundle!.channel,
					event_uuid: eventBundle!.uuid,
					profile: blProfileUser,
					registration_uuid: registrationId
				};
				return dispatch(createStripeIntent(options, data));
			}
		}
	}, [registrationStep, activeStep?.type, dispatch, language, eventBundle, blProfileUserToken, previewMode, isFinalStep, blProfileUser, registrationId]);

	useEffect(() => {
		const charge = async () => {
			if (!stripeElements || !blProfileUser) {
				return;
			}

			try {
				const card = stripeElements?.getElement?.(CardNumberElement);

				//this is overfiring
				if (!card || stripeIntentChecked.current) return;

				const res: PaymentIntentResult | undefined = await stripe?.confirmCardPayment(stripeIntent.clientSecret, {
					payment_method: {
						billing_details: {
							email: blProfileUser.email,
							address: {
								postal_code: stripeIntent.card_zip,
							},
							name: stripeIntent.card_name,
						},
						card,
					},
					receipt_email: blProfileUser.email,
				});

				if (!res) {
					showAlert({
						message: 'Unable to communicate with credit card processor. Please try again.',
						type: 'error',
						duration: 20000,
					});
				} else if (res.error) {
					showAlert({
						message: res.error.message,
						type: 'error',
						duration: 20000,
					});
				} else if (res.paymentIntent.status === 'succeeded') {
					dispatch(finishedStripePayment(res));

					//prevent over firing this - the useEffect is a little too greedy
					stripeIntentChecked.current = true;

					if (isFinalStep) {
						//need to send the email after payment was received. Re-register already exists and does exactly what we want here
						dispatch(reRegister({
							eventUuid: eventBundle!.uuid,
							email: blProfileUser.email,
							lang: language,
							timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
						}));
					}
				}
			} catch (e) {
				console.error('chargeError', e);
			}
		};

		if (stripeIntent) {
			charge();
		}
	}, [stripeIntent, blProfileUser, dispatch, stripe, stripeElements, eventBundle, language, isFinalStep]);

	const cancelSignIn = useCallback(() => {
		setSignIn(false);
	}, []);

	const handleSignIn = useCallback((e: any) => {
		e.preventDefault();
		setSignIn(true);
	}, []);

	useEffect(() => {
		if (!stripeIntentError) {
			return;
		}

		showAlert({
			message: stripeIntentError,
			type: 'error',
			duration: 20000,
		});
	}, [stripeIntentError]);

	const footerProps = useMemo(() => {
		return {
			isFirstStep,
			isFinalStep,
			onPrevStepClick: handlePrevStep,
			singleSignOns: singleSignOns || [],
			waiting: registering || completingProfile,
			error: registrationError
		};
	}, [handlePrevStep, isFinalStep, isFirstStep, singleSignOns, registering, completingProfile, registrationError]);

	if (isPreview && workingEvent && registration) {
		return (
			<RegistrationLayout
				registration={registration}
				startDate={startDate}
				template={template}
			>
				<div className="registration-logo">
					<img
						src={logo}
						alt=""
						aria-hidden="true"
					/>
				</div>
				{activeStep && (
					<RegistrationContent
						defaultValues={blProfileUser?.profile?.[workingEvent.channel] ?? {}}
						activeStep={activeStep}
						requiredFields={requiredFields || []}
						onSaveData={handleSaveData}
						footerProps={footerProps}
						template={template}
						registrationStep={registrationStep}
						steps={activeSteps}
						profile={blProfileUser}
						channel={workingEvent.channel}
						handleSignIn={handleSignIn}
						eventBundle={eventBundle}
					/>
				)}
			</RegistrationLayout>
		);
	}

	if (!registration || !eventBundle) {
		return <WaitingIndicator />;
	}

	const defaults = blProfileUser?.profile?.[eventBundle.channel] ?? {};
	if (selectedTicket) {
		defaults.ticket = selectedTicket;
	}

	return finishedRegistering && !mustVerify ? (
		<>
			{eventBundle.homepage ? (
				<Redirect to={location.state?.referrer ? location.state.referrer : `/${eventPath(eventName, uuid)}/${language}/home`} />
			) : (
				<Redirect to={location.state?.referrer ? location.state.referrer : `/${eventPath(eventName, uuid)}/${language}`} />
			)}
		</>
	) : (
		<RegistrationLayout
			registration={registration}
			startDate={startDate}
			template={template}
		>
			<div className="registration-logo">
				<img
					src={logo}
					alt=""
					aria-hidden="true"
				/>
			</div>
			{signIn ? (
				<>
					{mustVerify ? (
						<VerificationView template={template} />
					) : (
						<SignIn onPrevStepClick={cancelSignIn} template={template} />
					)}
				</>
			) : (
				<>
					{mustVerify && isFinalStep && (finishedRegistering || shouldRedirectToRegistration) ? (
						<VerificationView template={template} />
					) : (
						<>
							{activeStep && (
								<RegistrationContent
									defaultValues={defaults}
									activeStep={activeStep}
									requiredFields={requiredFields || []}
									onSaveData={handleSaveData}
									footerProps={footerProps}
									template={template}
									registrationStep={registrationStep}
									steps={activeSteps}
									profile={blProfileUser}
									channel={eventBundle.channel}
									handleSignIn={handleSignIn}
									eventBundle={eventBundle}
								/>
							)}
						</>
					)}
				</>
			)}


		</RegistrationLayout>
	);
};

const RegistrationWithStripe: React.FC<RegistrationPageProps> = ({ previewLanguage }): JSX.Element => {
	const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string);

	return (
		<Elements stripe={stripePromise}>
			<RegistrationPage previewLanguage={previewLanguage} />
		</Elements>
	);
};

export default RegistrationWithStripe;

export const VerificationView = ({ template }: { template: TemplateNames }): JSX.Element => {
	const { t } = useTranslation('homepage');
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);

	const registerAgain = useCallback(() => {
		removeStorageItem(`reg.${eventBundle?.uuid}`);
		removeStorageItem(`registration-step-${eventBundle?.uuid}`);
		removeStorageItem(`allowed.${eventBundle?.uuid}`);
		removeStorageItem('bl-profile');
		removeStorageItem('Bl-Profile-Token');
		removeStorageItem(`should-cont-reg-${eventBundle?.uuid}`);
		window.location.reload();
	}, [eventBundle]);

	return (
		<div className="registration-slider">
			<div className="registration-panel fixed">
				<form className="registration-form">
					<div className="registration-form-inner verification" style={{ overflow: 'hidden' }}>
						<TypographyItem className={classNames("evt-heading-2 stable registration-form-title", template)} tagName="h2">
							{t('registration.verificationHeader')}
						</TypographyItem>
						<TypographyItem tagName="p" className={classNames(template)} >
							{t("registration.verificationBody")}
						</TypographyItem>
					</div>
					<div className="registration-actions secondary">
						<div>
							<Button
								classButton={classNames('registration-button primary')}
								template={template}
								typeBtn="submit"
								onClick={registerAgain}
							>
								{t("registration.RegisterAgain")}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export const SignIn = ({ template, onPrevStepClick }: { template: TemplateNames, onPrevStepClick: () => void }): JSX.Element => {
	const { t } = useTranslation('homepage');
	const registering = useTypedSelector(state => state.LiveEventReducer.registering);
	const eventUuid = useTypedSelector(state => state.LiveEventReducer.eventBundle?.uuid);
	const dispatch = useDispatch();
	const [email, setEmail] = useState('');
	const [valid, setValid] = useState(Validation.normal);
	const { language } = useParams() as ParamsProps;

	const setValue = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(value);
		setValid(isValidEmail(value) ? Validation.ok : Validation.error);
	}, []);

	const handleSubmit = useCallback((e: React.FormEvent) => {
		e.preventDefault();

		if (isValidEmail(email)) {
			dispatch(reRegister({
				eventUuid: eventUuid!,
				email,
				lang: language,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
			}));
		}
	}, [email, eventUuid, dispatch, language]);

	return (
		<div className="registration-slider">
			<div className="registration-panel fixed">
				<form className="registration-form" onSubmit={handleSubmit}>
					<div className="registration-form-inner verification" onSubmit={handleSubmit} style={{ overflow: 'hidden' }}>
						<TypographyItem className={classNames("evt-heading-2 stable registration-form-title", template)} tagName="h2">
							{t('registration.verificationHeader')}
						</TypographyItem>
						<TypographyItem tagName="p" className={classNames(template)} >
							{t("registration.signinBody")}
						</TypographyItem>
						<br />
						<div className={classNames('evt-field-wrapper', template)}>
							<TextInput
								placeholder={t("registration.registration_fields.3")}
								label={t("registration.registration_fields.3")}
								required={true}
								onChange={setValue}
								email={true}
								valid={valid}
							/>
							{!valid && <p className="registration-error">{t("registration.Please enter a valid email address")}</p>}
						</div>
					</div>
					<div className="registration-actions secondary">
						<Button
							isRound={true}
							classButton={classNames('registration-button secondary')}
							template={template}
							onClick={onPrevStepClick}
							typeBtn="button"
						>
							<EIcon name="keyboard-arrow-left" />
						</Button>

						<div>
							<Button
								classButton={classNames('registration-button primary')}
								template={template}
								typeBtn="submit"
								isDisabled={!email || registering}
							>
								{t("Submit")}
							</Button>
						</div>
					</div>
				</form >
			</div>
		</div>
	);
};


interface ContentProps {
	fields: IRegistrationFormField[];
	defaultValues: SubmissionValues;
	onSaveData: (data: SubmissionValues) => void;
	footerProps: RegistrationFooterProps;
	template: TemplateNames;
	profile: BlProfile | null;
	channel: number;
	isProfile?: boolean;
	ticketing?: TicketingSet[];
	isDisplayOnly?: boolean;
	handleSignIn?: (e: any) => void;
	eventBundle?: BrandliveEvent | null;
}

type TRegistrationData = {
	[name in RegistrationQuestionType]: string | number | Date | boolean;
};
const RegistrationContent = ({
	activeStep,
	requiredFields,
	footerProps,
	defaultValues,
	onSaveData,
	template,
	registrationStep,
	steps,
	profile,
	channel,
	handleSignIn,
	eventBundle,
}: {
	activeStep: RegistrationStep;
	requiredFields: number[];
	footerProps: RegistrationFooterProps;
	onSaveData: (props: SubmissionValues) => void;
	defaultValues: SubmissionValues;
	template: TemplateNames;
	registrationStep: number;
	steps: RegistrationStep[];
	profile: BlProfile | null;
	channel: number;
	handleSignIn?: (e: any) => void;
	eventBundle?: BrandliveEvent | null;
}): JSX.Element => {
	const { questions, type } = activeStep;
	const { language }: any = useParams();

	const fields = useMemo<IRegistrationFormField[]>(() => {
		return parseFields({
			questions: questions || [],
			requiredFields: requiredFields || [],
			language
		});
	}, [questions, requiredFields, language]);

	const stepsMap = ["one", "two", "three", "four", "five"];

	const ticketingStep = steps.find(step => step.type == RegistrationStepType.ticketing && step.isOn);

	const getStep = (type: RegistrationStepType) => {
		switch (type) {
			case RegistrationStepType.ticketing:

				return (
					<TicketingContent
						defaultValues={defaultValues}
						template={template}
						fields={fields}
						footerProps={footerProps}
						onSaveData={onSaveData}
						profile={profile}
						channel={channel}
						ticketing={ticketingStep?.ticketing}
					/>
				);
			case RegistrationStepType.avatar:
				return (
					<AvatarContent
						defaultValues={defaultValues}
						template={template}
						fields={fields}
						onSaveData={onSaveData}
						footerProps={footerProps}
						profile={profile}
						channel={channel}
					/>
				);
			case RegistrationStepType.profile:
				return (
					<GeneralContent
						defaultValues={defaultValues}
						template={template}
						fields={fields}
						footerProps={footerProps}
						onSaveData={onSaveData}
						profile={profile}
						channel={channel}
						isProfile={true}
						handleSignIn={handleSignIn}
					/>
				);
			case RegistrationStepType.general:
				return (
					<GeneralContent
						defaultValues={defaultValues}
						template={template}
						fields={fields}
						footerProps={footerProps}
						onSaveData={onSaveData}
						profile={profile}
						channel={channel}
						ticketing={ticketingStep?.ticketing}
						handleSignIn={handleSignIn}
						eventBundle={eventBundle}
					/>
				);
			default:
				return <>Not implemented</>;
		}
	};

	return (
		<div className={classNames("registration-slider", stepsMap[registrationStep])}>
			{steps.map((step, index) => (
				<div className={classNames("registration-panel", { active: type === step.type })} key={index}>
					{getStep(step.type)}
				</div>
			))}
		</div>
	);
};

interface SubmissionValues {
	[key: string]: string | boolean;
}

function validateField(field: IRegistrationFormField, value: string): boolean {
	if (field.field_id === 3) {
		return isValidEmail(value);
	}

	if (field.required) {
		return !!value;
	} else {
		return true;
	}
}


const GeneralContent = ({
	fields,
	template,
	onSaveData,
	footerProps,
	defaultValues,
	profile,
	channel,
	isProfile = false,
	ticketing,
	handleSignIn,
	eventBundle
}: ContentProps): JSX.Element => {
	const [fieldValues, setFieldValues] = useState<SubmissionValues>(defaultValues);
	const [invalidFields, setInvalidFields] = useState<number[]>([]);
	const profileValues = profile?.profile?.[channel];
	const [selectedTicket, setSelectedTicket] = useState<TicketingSet>();
	const [soldOut, setSoldOut] = useState<boolean>(false);
	const [ticketError, setTicketError] = useState<boolean>(false);
	const { t } = useTranslation("homepage");

	const {
		LiveEventReducer: {
			numberOfTicketsPurchased,
		}
	} = useSelector((event: EventsState) => event);

	const requiredFieldIds = useMemo(() => (fields
		.filter(field => field.required)
		.map(field => field.field_id)
	), [fields]);

	useEffect(() => {
		//on load, iterate over fields
		for (const field of fields) {
			//if this is a select type, we need to set the selected value to the field's initial value
			if (field.type === QuestionType.select) {
				//set values for submission
				setFieldValues((values) => {
					//if they have a previously selected value for this field from their token, select it here. Otherwise, pick the first option for the field as selected
					values[field.field_id] = profileValues?.[field.field_id] ?? (field.options?.length && field.options[0]?.value) ?? '';
					return { ...values };
				});
			}
		}
		//only doing this on first load, we don't want to override any selections the user might have made
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!ticketing || !numberOfTicketsPurchased || Object.keys(numberOfTicketsPurchased).length === 0) {
			return;
		}

		const totalAvailable = ticketing.reduce((a: number, c: TicketingSet) => {
			return a + c.quantity;
		}, 0);

		const totalSold = Object.values(numberOfTicketsPurchased).reduce((a: number, c: number) => {
			return a + c;
		}, 0);

		if (totalSold >= totalAvailable) {
			setSoldOut(true);
		}
	}, [numberOfTicketsPurchased, ticketing]);

	const setValue = (field: number) => {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
			//pull field by id
			const _field = fields.find(_field => _field.field_id === field);

			//check if entry is valid
			const isValid = validateField(_field!, value);

			//hold ids of invalid fields
			setInvalidFields((fields) => {
				if (isValid) {
					return fields.filter(_field => _field !== field);
				} else {
					return Array.from(new Set([...fields, field]).values());
				}
			});

			//set values for submission
			setFieldValues((values) => {
				values[field] = value;
				return { ...values };
			});
		};
	};

	//checkbox returns different event type than text/select fields, needs different handler
	const setCheckboxValue = (field: number) => {
		return (isChecked: boolean) => {
			//pull field by id
			const _field = fields.find(_field => _field.field_id === field);

			//if checkbox is required, user must check it to pass
			setInvalidFields((fields) => {
				if (_field?.required && !isChecked) {
					return Array.from(new Set([...fields, field]).values());
				} else {
					return fields.filter(_field => _field !== field);
				}
			});

			//set values for submission
			setFieldValues((values) => {
				values[field] = isChecked;
				return { ...values };
			});
		};
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const values = fieldValues;

		// prevent submit and show errors for missing required info, event sold out, and no ticket selected
		const missingRequired = requiredFieldIds.filter(id => !values[id]);
		setInvalidFields([...invalidFields, ...missingRequired]);
		const missingTicket = ticketing && ticketing.length > 0 && !selectedTicket;
		setTicketError(missingTicket as boolean);
		if (missingTicket || soldOut || missingRequired.length) {
			return false;
		}

		if (selectedTicket?.uuid) {
			values.ticket = selectedTicket.uuid;
		}

		onSaveData(values);
	};

	const ticketChange = (uuid: string) => {
		const newTicket = ticketing!.find((ticket: TicketingSet) => ticket.uuid == uuid);

		setSelectedTicket(newTicket);
	};

	const renderTicket = (ticket: TicketingSet) => {
		const numberPurchased = numberOfTicketsPurchased?.[ticket.uuid];

		if (numberPurchased && numberPurchased >= ticket.quantity) {
			return (
				<div className="ticket-sold-out">
					<p>{t('registration.soldOut')}</p>
				</div>
			);
		}

		return (
			<div className="ticket-checkbox">
				<CustomCheckbox
					name={`ticket-${ticket.uuid}`}
					size={26}
					onChange={() => ticketChange(ticket.uuid)}
					checked={ticket.uuid == selectedTicket?.uuid}
					selectedIconName={ICONS.SQUARE_CHECKBOX_SELECTED}
				/>
			</div>
		);
	};

	const renderTickets = (tickets: TicketingSet[]) => {
		return tickets.map((ticket: TicketingSet) => (
			<div key={`ticket-${ticket.uuid}`} className="ticket">
				<div className="ticket-info">
					<p className="ticket-name">{ticket.name}</p>
					<p className="ticket-description">{ticket.description}</p>
					<p className="ticket-price">${ticket.price.toFixed(2)}</p>
				</div>
				{renderTicket(ticket)}
			</div>
		));
	};

	const renderHeader = () => {
		if (soldOut) {
			return t('registration.soldOutHeader');
		}

		if (!isProfile) {
			return t("registration.Register for the event");
		}

		return t("registration.Tell us about yourself");
	};

	const hasEmailField = useMemo(() => {
		return fields.some((field) => field.field_id === 3);
	}, [fields]);

	return (
		<form className="registration-form" onSubmit={handleSubmit} onKeyPress={(e) => { if (e.key === "Enter") e.preventDefault(); }}>
			<div className="registration-form-inner">
				<TypographyItem className="evt-heading-2 stable registration-form-title" tagName="h2">
					{renderHeader()}
				</TypographyItem>
				{hasEmailField && (
					<button onClick={handleSignIn} role="button" type="button" className="no-style sign-in">{t("registration.AlreadyRegistered")}</button>
				)}
				{soldOut && <p className="sold-out">{t('registration.soldOutSubHeader')}</p>}
				<div className="sold-out-container">
					{soldOut && <div className="sold-out"></div>}
					{fields.map((field: IRegistrationFormField, index) => {
						switch (field.type) {
							//these are all text fields
							case QuestionType.email:
							case QuestionType.numeric:
							case QuestionType.text: {
								// TODO: instead of label, we need to add Gate types to questions for better identifying
								if (field.label === 'Passcode' && eventBundle?.registration_settings?.type !== RegistrationTypes.gated) return null;
								return (
									<div key={index + field.label + field.field_id} className={classNames('evt-field-wrapper', template)}>
										<TextInput
											// below is correct, I just can't convince typescript that it is, hence the any
											label={field.global ? t(`registration.registration_fields.${field.field_id}` as any) : field.label}
											placeholder={field.global ? t(`registration.registration_fields.${field.field_id}` as any) : field.placeholder}
											required={field.required}
											defaultValue={profileValues?.[field.field_id]}
											onChange={setValue(field.field_id)}
											email={field.type === QuestionType.email}
											numeric={field.type === QuestionType.numeric}
											name={field.field_id.toString()}
											valid={invalidFields.includes(field.field_id) ? Validation.error : (fieldValues[field.field_id] ? Validation.ok : Validation.normal)}
										/>
										{invalidFields.includes(field.field_id) && (
											<>
												{field.type === QuestionType.email ? (
													<p className="registration-error">{t("registration.Please enter a valid email address")}</p>
												) : (
													<p className="registration-error">{t("registration.Field is required")}</p>
												)}
											</>
										)}
									</div>
								);
							}

							case QuestionType.checkbox: {
								return (
									<div key={index + field.label + field.field_id} className={classNames('evt-field-wrapper', template)}>
										<CustomCheckbox
											key={index}
											label={field.label}
											onChange={setCheckboxValue(field.field_id)}
											defaultValue={profileValues?.[field.field_id]}
											checked={!!fieldValues[field.field_id]}
											name={field.field_id.toString()}
										/>
										{invalidFields.includes(field.field_id) && (
											<p className="registration-error">{t("registration.Field is required")}</p>
										)}
									</div>
								);
							}
							case QuestionType.select: {
								return (
									<div key={index + field.label + field.field_id} className={classNames('evt-field-wrapper', template)}>
										<SelectNative
											options={field.options}
											label={field.label}
											required={field.required}
											defaultValue={profileValues?.[field.field_id]}
											onChange={setValue(field.field_id)}
											name={field.field_id.toString()}
										/>
										{invalidFields.includes(field.field_id) && (
											<p className="registration-error">{t("registration.Field is required")}</p>
										)}
									</div>
								);
							}
							case QuestionType.textarea: {
								return (
									<div key={index + field.label + field.field_id} className={classNames('evt-field-wrapper', template)}>
										<Textarea
											label={field.global ? t(`registration.registration_fields.${field.field_id}` as any) : field.label}
											placeholder={field.global ? t(`registration.registration_fields.${field.field_id}` as any) : field.placeholder}
											required={field.required}
											defaultValue={profileValues?.[field.field_id]}
											onChange={setValue(field.field_id)}
											name={field.field_id.toString()}
											valid={invalidFields.includes(field.field_id) ? Validation.error : (fieldValues[field.field_id] ? Validation.ok : Validation.normal)}
										></Textarea>

										{invalidFields.includes(field.field_id) && (
											<p className="registration-error">{t("registration.Field is required")}</p>
										)}
									</div>
								);
							}
						}
					})}
					{ticketing && ticketing.length > 0 && (
						<div className={classNames('evt-field-wrapper', template)}>
							<div className="ticketing">{renderTickets(ticketing)}</div>
							{ticketError && (
								<p className="registration-error">{t("registration.ticketRequired")}</p>
							)}
						</div>
					)}
				</div>
			</div>
			<RegistrationFooter
				{...footerProps}
				waiting={footerProps.waiting || invalidFields.length > 0 || soldOut}
				template={TemplateClassNames[template]}
			/>
		</form>
	);
};

export const AvatarContent = ({
	footerProps,
	template,
	onSaveData,
	isDisplayOnly = false
}: ContentProps): JSX.Element => {
	const {
		LiveEventReducer: {
			blProfileUser,
			eventBundle,
			blProfileUserToken
		}
	} = useTypedSelector(state => state);

	const uploadRef = useRef<HTMLInputElement | null>(null);
	const [uploading, setUploading] = useState(false);
	const channel = eventBundle?.channel ?? 0;
	const profile = blProfileUser?.profile?.[channel];
	const [image, setImage] = useState<string | null>(profile?.avatar ?? null);

	const { t } = useTranslation("homepage");

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (image)
			onSaveData({ avatar: image });
		else
			onSaveData({});
	};

	const upload = (e: any) => {
		if (isDisplayOnly) return null;
		e.preventDefault();
		uploadRef.current?.click();
	};

	const handleSelect = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		if (isDisplayOnly) return null;
		if (target.files && target.files[0]) {
			setUploading(true);
			try {
				const file = target.files[0];
				const token = blProfileUserToken;

				const result = jwt.decode(token!) as BlProfile;

				const prefix = `uploads/${result.bl_profile}/profile`;

				const nameParts = file.name.split(".");

				const suffix = nameParts[nameParts.length - 1];

				const endpoint = await GetUploadEndpoint(token!, prefix, `${uuidv4()}.${suffix}`, false, true);

				await UploadToS3(endpoint.url, file, endpoint.fields, undefined, `${uuidv4()}.${suffix}`);

				await MakeItemPublic(token!, endpoint.fields.Key, true);

				setImage(`https://assets.brandnot.live/${endpoint.fields.Key}`);
			} catch (e) {
				console.error(e);
			} finally {
				setUploading(false);
			}
		}
	};

	const initials = useMemo(() => {
		if (profile) {
			const first_name = profile[BlProfileValues.firstName];
			const last_name = profile[BlProfileValues.lastName];
			const email = profile[BlProfileValues.email];

			let str = '';
			if (first_name && last_name) {
				if (first_name?.[0]) {
					str += first_name[0];
				}

				if (last_name?.[0]) {
					str += last_name[0];
				}
			} else if (email) {
				str += email[0];
			}

			return str;
		} else {
			return "";
		}
	}, [profile]);

	return (
		<form className="registration-form" onSubmit={handleSubmit}>
			{/* placeholder, eventually this will be the image the user uploads */}
			<div className="registration-form-inner">
				<TypographyItem className="evt-heading-2 stable registration-form-title" tagName="h2">
					{uploading ? t("Uploading...") : t("registration.Upload your Avatar")}
				</TypographyItem>
				<div className="registration-avatar" style={image ? { backgroundImage: `url(${image})` } : {}}>
					{!image && <span className="registration-avatar-name">{initials}</span>}
					<input onChange={handleSelect} ref={uploadRef} type="file" style={{ display: 'none' }} />
					<button role="button" className="avatar-upload" onClick={upload} disabled={isDisplayOnly}>
						<EIcon name="camera" size="large" aria-hidden="true" />
					</button>
				</div>
			</div>
			{!isDisplayOnly && <RegistrationFooter
				{...footerProps}
				template={TemplateClassNames[template]}
			/>}
		</form>
	);
};

const StripeInput = (props: any) => {
	const { label, template, children, valid = Validation.normal } = props;
	return (
		<div className={classNames('evt-field-wrapper', template)}>
			<Field>
				<div className={classNames('field-group', 'text', valid)}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<FieldLabel label={label} required />
					</div>
					<div className={classNames('stripe-wrapper', template, valid)}>
						{children}
					</div>
				</div>
			</Field>
		</div>
	);
};

const TicketingContent = ({ footerProps, template, ticketing, defaultValues, onSaveData, channel }: ContentProps): JSX.Element => {
	const {
		LiveEventReducer: {
			blProfileUser,
			eventBundle,
			registrationStep
		},
		CreateEventReducer: {
			previewMode,
		}
	} = useSelector((event: EventsState) => event);

	const [fieldValues, setFieldValues] = useState<any | null>({});
	const [errors, setErrors] = useState<any | null>({});
	const [cardFields, setCardFields] = useState<any | null>({});
	const elements = useElements();
	const { t } = useTranslation("homepage");

	const dispatch = useDispatch();

	const colors: { [key: string]: string } = {};

	if (eventBundle?.settings?.design?.colors?.colors) {
		Object.keys(eventBundle.settings.design.colors.colors).forEach(color => {
			colors[color] = parseColor(eventBundle.settings.design.colors.colors[color]);
		});
	}

	useEffect(() => {
		setFieldValues((values: any) => {
			values.ticket = blProfileUser?.profile?.[channel].ticket;
			return { ...values };
		});
	}, [blProfileUser, channel]);

	if (!elements || !ticketing) {
		return <div></div>;
	}

	const selectedTicket = ticketing.find((ticket: TicketingSet) => ticket.uuid == defaultValues.ticket);

	const creditCardSubmitHandler = async (e: React.FormEvent) => {
		e.preventDefault();
		if (previewMode === EPreviewTypes.Registration) {
			return dispatch(setRegistrationStep(registrationStep + 1));
		}

		// all fields must be completed before submitting
		const _errors = { ...errors };
		if (!fieldValues.card_name) _errors.card_name = true;
		if (!fieldValues.card_zip || fieldValues.card_zip.length !== 5) _errors.card_zip = true;
		if (!cardFields.cardNumber) _errors.cardNumber = true;
		if (!cardFields.cardExpiry) _errors.cardExpiry = true;
		if (!cardFields.cardCvc) _errors.cardCvc = true;
		setErrors(_errors);
		if (Object.keys(errors).length !== 0) return false;

		onSaveData(fieldValues);
	};

	const setValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValues((values: any) => {
			values[e.target.name] = e.target.value;
			return { ...values };
		});

		setErrors((errors: any) => {
			const _errors = { ...errors };

			if (!fieldValues[e.target.name] || fieldValues[e.target.name].length === 0) {
				_errors[e.target.name] = true;
			} else {
				delete _errors[e.target.name];
			}

			if (e.target.name === 'card_zip' && fieldValues.card_zip.length !== 5) {
				_errors.card_zip = true;
			} else if (e.target.name === 'card_zip') {
				delete _errors.card_zip;
			}

			return _errors;
		});
	};

	const elementChangeHandler = (e: any) => {
		setErrors((errors: any) => {
			const _errors = { ...errors };

			if (typeof e.error !== 'undefined') {
				_errors[e.elementType] = true;
			} else {
				delete _errors[e.elementType];
			}

			return _errors;
		});

		setCardFields((cardFields: any) => {
			const _cardFields = { ...cardFields };

			if (e.complete) {
				_cardFields[e.elementType] = true;
			} else {
				delete _cardFields[e.elementType];
			}

			return _cardFields;
		});
	};

	const options: StripeCardNumberElementOptions = {
		style: {
			base: {
				color: template === TemplateNames.O2 ? colors.containerColor : colors.bodyTextColor,
				fontSize: "15px"
			},
			invalid: {
				color: template === TemplateNames.O2 ? colors.containerColor : colors.bodyTextColor,
			}
		}
	};

	return (
		<form name="credit-card" className="registration-form" onSubmit={creditCardSubmitHandler}>
			<div className="ticketing">
				<h2>Checkout</h2>
				<div className="credit-card">
					<h4>{selectedTicket?.name} ${selectedTicket?.price.toFixed(2)}</h4>
					<div className={classNames('evt-field-wrapper', TemplateClassNames[template])}>
						<TextInput label={t('registration.nameOnCard')} id="card_name" name="card_name" onChange={setValue} required valid={errors.card_name ? Validation.error : (fieldValues.card_name ? Validation.ok : Validation.normal)} />
						{errors.card_name && (<p className="registration-error">{t("registration.Field is required")}</p>)}
					</div>
					<StripeInput label={t('registration.cardNumber')} template={TemplateClassNames[template]} valid={errors.cardNumber ? Validation.error : (cardFields.cardNumber ? Validation.ok : Validation.normal)}>
						<CardNumberElement onChange={elementChangeHandler} options={options} />
					</StripeInput>
					{errors.cardNumber && (<p className="registration-error">{t("registration.cardNumberError")}</p>)}
					<div className="small-cols">
						<StripeInput label={t('registration.expirationDate')} template={TemplateClassNames[template]} valid={errors.cardExpiry ? Validation.error : (cardFields.cardExpiry ? Validation.ok : Validation.normal)}>
							<CardExpiryElement onChange={elementChangeHandler} options={options} />
						</StripeInput>
						<StripeInput label="CVC" template={TemplateClassNames[template]} valid={errors.cardCvc ? Validation.error : (cardFields.cardCvc ? Validation.ok : Validation.normal)}>
							<CardCvcElement onChange={elementChangeHandler} options={options} />
						</StripeInput>
						<div className={classNames('evt-field-wrapper', TemplateClassNames[template])}>
							<TextInput label={t('registration.zipCode')} id="card_zip" name="card_zip" onChange={setValue} required valid={errors.card_zip ? Validation.error : (fieldValues.card_zip ? Validation.ok : Validation.normal)} numeric />
						</div>
					</div>
					{errors.cardExpiry && (<p className="registration-error">{t("registration.expirationDateError")}</p>)}
					{errors.cardCvc && (<p className="registration-error">{t("registration.codeError")}</p>)}
					{errors.card_zip && (<p className="registration-error">{t("registration.zipCodeError")}</p>)}
				</div>
				<RegistrationFooter
					{...footerProps}
					template={TemplateClassNames[template]}
					notSkippable
				/>
			</div>
		</form>
	);
};
